import { API_BASE_URL } from '../config/api.config.js';
import logger from '@/utils/logger';
import { getToken } from '@/utils/storage';

const getAuthToken = () => {
  const token = getToken();
  if (token) return token;

  if (import.meta.env.DEV || window.location.hostname === 'localhost') {
    logger.debug('🧪 UnifiedApi - Mode développement détecté, utilisation du token de test');
    return 'test-token-for-development';
  }

  logger.warn('⚠️ UnifiedApi - Aucun token valide trouvé');
  return null;
};

// Fonction utilitaire pour les requêtes API avec authentification automatique
const apiRequest = async (method, endpoint, options = {}) => {
  const { data, params, headers = {}, skipAuth = false } = options;

  // Ajouter automatiquement le token d'authentification (sauf pour les endpoints publics non authentifiés)
  const token = skipAuth ? null : getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    // logger.debug('🔐 UnifiedApi - Token ajouté aux headers');
    // logger.debug('🔍 Token exact envoyé:', token);
    // logger.debug('🔍 Token length:', token.length);
    // logger.debug('🔍 Token starts with:', token.substring(0, 20));
    // logger.debug('🔍 Header Authorization exact:', `Bearer ${token}`);
    // logger.debug('🔍 Headers complets à envoyer:', headers);
  } else if (!skipAuth) {
    logger.warn('⚠️ UnifiedApi - Pas de token disponible pour la requête');
  }

  // Construction de l'URL avec paramètres de requête
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // logger.debug(`🔗 API Request: ${method} ${url}`, token ? '🔐 Avec token' : '⚠️ Sans token');

  try {
    // Nettoyer les headers de toute valeur invalide
    const cleanHeaders = {};
    Object.entries(headers).forEach(([key, value]) => {
      // Validation stricte pour éviter l'erreur "Invalid value"
      if (
        value !== null &&
        value !== undefined &&
        value !== 'null' &&
        value !== 'undefined' &&
        typeof value === 'string'
      ) {
        const trimmedValue = value.trim();
        if (trimmedValue.length > 0 && !trimmedValue.includes('\n') && !trimmedValue.includes('\r')) {
          cleanHeaders[key] = trimmedValue;
        }
      }
    });

    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...cleanHeaders,
      },
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
      config.body = JSON.stringify(data);
    }

    // logger.debug('📤 Fetch config:', {
    //   url,
    //   method,
    //   headers: config.headers,
    //   hasBody: !!config.body
    // });

    const response = await fetch(url, config);

    // logger.debug(`📥 Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`❌ API Error ${response.status}:`, errorText);

      // Extraire le message du JSON si possible
      let errorMessage = errorText;
      let parsedData = null;

      try {
        parsedData = JSON.parse(errorText);
        // Certaines routes (ex: middleware/validation.js) renvoient le message lisible
        // dans "error" plutôt que "message" — on essaie les deux avant le texte brut.
        if (parsedData && parsedData.message) {
          errorMessage = parsedData.message;
        } else if (parsedData && typeof parsedData.error === 'string') {
          errorMessage = parsedData.error;
        }
      } catch {
        // Si ce n'est pas du JSON, garder le texte brut
      }

      const err = new Error(errorMessage);
      err.response = {
        status: response.status,
        data: parsedData || errorText,
      };
      throw err;
    }

    const result = await response.json();
    // logger.debug('✅ API Success:', result);
    return result;
  } catch (error) {
    logger.error('❌ API Request failed:', error);
    throw error;
  }
};

// API unifiée
const unifiedApi = {
  // Méthodes génériques
  get: (endpoint, options) => apiRequest('GET', endpoint, options),
  post: (endpoint, data, options = {}) => apiRequest('POST', endpoint, { ...options, data }),
  put: (endpoint, data, options = {}) => apiRequest('PUT', endpoint, { ...options, data }),
  delete: (endpoint, options) => apiRequest('DELETE', endpoint, options),
  patch: (endpoint, data, options = {}) => apiRequest('PATCH', endpoint, { ...options, data }),

  // Gestion des arrêts
  stops: {
    getAll: (params) => apiRequest('GET', '/admin/stops', { params }),
    getAllUnlimited: (params) => apiRequest('GET', '/admin/stops/all', { params }),
    getById: (id) => apiRequest('GET', `/admin/stops/${id}`),
    create: (data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.type !== undefined) {
        headers['x-feature-type'] = featureFlags.type.toString();
      }
      if (featureFlags.avance !== undefined) {
        headers['x-feature-avance'] = featureFlags.avance.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags:', headers);
      return apiRequest('POST', '/admin/stops', { data, headers });
    },
    update: (id, data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.type !== undefined) {
        headers['x-feature-type'] = featureFlags.type.toString();
      }
      if (featureFlags.avance !== undefined) {
        headers['x-feature-avance'] = featureFlags.avance.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags (update):', headers);
      return apiRequest('PUT', `/admin/stops/${id}`, { data, headers });
    },
    delete: (id) => apiRequest('DELETE', `/admin/stops/${id}`),
    getStats: () => apiRequest('GET', '/admin/stops/stats'),

    // Import/Export
    importFromCSV: (data) => apiRequest('POST', '/admin/stops/import', { data }),
    exportToCSV: (params) => apiRequest('GET', '/admin/stops/export', { params }),
    previewDelete: (id) => apiRequest('GET', `/admin/stops/${id}/preview-delete`),
  },

  // Gestion des trajets
  routes: {
    getAll: (params) => apiRequest('GET', '/admin/routes', { params }),
    getVisualization: () => apiRequest('GET', '/admin/routes/visualization'),
    getById: (id) => apiRequest('GET', `/admin/routes/${id}`),
    getStops: (id, options = {}) =>
      apiRequest('GET', `/admin/routes/${id}/stops`, {
        params: { direction: options.direction },
      }),
    create: (data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.horaires !== undefined) {
        headers['x-feature-horaires'] = featureFlags.horaires.toString();
      }
      if (featureFlags.optimisation !== undefined) {
        headers['x-feature-optimisation'] = featureFlags.optimisation.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags routes:', headers);
      return apiRequest('POST', '/admin/routes', { data, headers });
    },
    update: (id, data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.horaires !== undefined) {
        headers['x-feature-horaires'] = featureFlags.horaires.toString();
      }
      if (featureFlags.optimisation !== undefined) {
        headers['x-feature-optimisation'] = featureFlags.optimisation.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags routes (update):', headers);
      return apiRequest('PUT', `/admin/routes/${id}`, { data, headers });
    },
    delete: (id) => apiRequest('DELETE', `/admin/routes/${id}`),
    getStats: () => apiRequest('GET', '/admin/routes/stats'),

    getMetrics: (id) => apiRequest('GET', `/admin/routes/${id}/metrics`),
    getSuggestions: (id) => apiRequest('GET', `/admin/routes/${id}/suggestions`),

    // Gestion des horaires
    createSchedule: (id, data) => apiRequest('POST', `/admin/routes/${id}/schedule`, { data }),
    getSchedule: (id) => apiRequest('GET', `/admin/routes/${id}/schedule`),
    updateSchedule: (id, data) => apiRequest('PUT', `/admin/routes/${id}/schedule`, { data }),

    // Analytics et performance
    recordPerformance: (id, data) => apiRequest('POST', `/admin/routes/${id}/performance`, { data }),
    getAnalytics: (id) => apiRequest('GET', `/admin/routes/${id}/analytics`),
    compareRoutes: (params) => apiRequest('GET', '/admin/routes/compare', { params }),
    getIssues: (id) => apiRequest('GET', `/admin/routes/${id}/issues`),
  },

  // Gestion des étudiants
  students: {
    getAll: (params) => apiRequest('GET', '/admin/students', { params }),
    getAllUnlimited: (params) => apiRequest('GET', '/admin/students/all', { params }),
    getById: (id) => apiRequest('GET', `/admin/students/${id}`),
    create: (data) => apiRequest('POST', '/admin/students', { data }),
    update: (id, data) => apiRequest('PUT', `/admin/students/${id}`, { data }),
    delete: (id) => apiRequest('DELETE', `/admin/students/${id}`),
    getStats: () => apiRequest('GET', '/admin/students/stats'),
    assignTransport: (studentId, transportData) =>
      apiRequest('PUT', `/admin/students/${studentId}/transport`, {
        data: transportData,
      }),
    assignParent: (studentId, parentData) =>
      apiRequest('PUT', `/admin/students/${studentId}/parent`, {
        data: parentData,
      }),
    getParentRelations: (studentId) => apiRequest('GET', `/admin/students/${studentId}/parents`),

    // TICKET-06: Prévisualisation et changement de statut avec cascade
    previewDelete: (id) => apiRequest('GET', `/admin/students/${id}/preview-delete`),
    previewBulkDelete: (studentIds) =>
      apiRequest('POST', `/admin/students/preview-bulk-delete`, {
        data: { studentIds },
      }),
    previewStatusChange: (id, status) =>
      apiRequest('GET', `/admin/students/${id}/preview-status`, {
        params: { status },
      }),
    updateStatus: (id, status) => apiRequest('PUT', `/admin/students/${id}/status`, { data: { status } }),
  },

  // Gestion des chauffeurs
  drivers: {
    getAll: (params) => apiRequest('GET', '/admin/drivers', { params }),
    getById: (id) => apiRequest('GET', `/admin/drivers/${id}`),
    create: (data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.permis !== undefined) {
        headers['x-feature-permis'] = featureFlags.permis.toString();
      }
      if (featureFlags.professionnel !== undefined) {
        headers['x-feature-professionnel'] = featureFlags.professionnel.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags:', headers);
      return apiRequest('POST', '/admin/drivers', { data, headers });
    },
    update: (id, data) => apiRequest('PUT', `/admin/drivers/${id}`, { data }),
    delete: (id) => apiRequest('DELETE', `/admin/drivers/${id}`),
    softDelete: (id) => apiRequest('DELETE', `/admin/drivers/${id}/soft`),

    // Nouvelles fonctionnalités d'assignation
    assign: (id, data) => apiRequest('POST', `/admin/drivers/${id}/assign`, { data }),
    unassign: (id, data) => apiRequest('DELETE', `/admin/drivers/${id}/assign`, { data }),

    // Assignation spécifique bus et circuit
    assignBusAndRoute: (driverId, data) => {
      logger.debug('🚗 [unifiedApi] assignBusAndRoute appelé avec:', {
        driverId,
        data,
      });
      return apiRequest('PUT', `/admin/drivers/${driverId}/assignment`, {
        data,
      });
    },

    // Gestion des horaires
    createSchedule: (id, data) => apiRequest('POST', `/admin/drivers/${id}/schedule`, { data }),
    getSchedule: (id) => apiRequest('GET', `/admin/drivers/${id}/schedule`),
    updateSchedule: (id, data) => apiRequest('PUT', `/admin/drivers/${id}/schedule`, { data }),

    // Suivi des performances
    recordPerformance: (id, data) => apiRequest('POST', `/admin/drivers/${id}/performance`, { data }),
    getPerformanceReport: (id, params) => apiRequest('GET', `/admin/drivers/${id}/performance/report`, { params }),

    // Suivi de présence
    recordAttendance: (id, data) => apiRequest('POST', `/admin/drivers/${id}/attendance`, { data }),

    previewDelete: (id) => apiRequest('GET', `/admin/drivers/${id}/preview-delete`),
  },

  // Gestion des parents
  parents: {
    getAll: (params) => apiRequest('GET', '/admin/parents', { params }),
    getAllUnlimited: (params) => apiRequest('GET', '/admin/parents/all', { params }),
    getById: (id) => apiRequest('GET', `/admin/parents/${id}`),
    create: (data) => apiRequest('POST', '/admin/parents', { data }),
    update: (id, data) => apiRequest('PUT', `/admin/parents/${id}`, { data }),
    delete: (id) => apiRequest('DELETE', `/admin/parents/${id}`),
    getStats: () => apiRequest('GET', '/admin/parents/stats'),
    previewDelete: (id) => apiRequest('GET', `/admin/parents/${id}/preview-delete`),
  },

  // Gestion des bus
  buses: {
    getAll: (params) => apiRequest('GET', '/admin/buses', { params }),
    getById: (id) => apiRequest('GET', `/admin/buses/${id}`),
    create: (data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.technique !== undefined) {
        headers['x-feature-technique'] = featureFlags.technique.toString();
      }
      if (featureFlags.maintenance !== undefined) {
        headers['x-feature-maintenance'] = featureFlags.maintenance.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags buses:', headers);
      return apiRequest('POST', '/admin/buses', { data, headers });
    },
    update: (id, data, featureFlags = {}) => {
      const headers = {};
      if (featureFlags.technique !== undefined) {
        headers['x-feature-technique'] = featureFlags.technique.toString();
      }
      if (featureFlags.maintenance !== undefined) {
        headers['x-feature-maintenance'] = featureFlags.maintenance.toString();
      }
      logger.debug('📤 [Frontend] Envoi des feature flags buses (update):', headers);
      return apiRequest('PUT', `/admin/buses/${id}`, { data, headers });
    },
    delete: (id) => apiRequest('DELETE', `/admin/buses/${id}`),
    getStats: () => apiRequest('GET', '/admin/buses/stats'),
    previewDelete: (id) => apiRequest('GET', `/admin/buses/${id}/preview-delete`),
  },

  // Suivi GPS
  gps: {
    getAll: (params) => apiRequest('GET', '/gps/tracking', { params }),
    getByDriver: (driverId) => apiRequest('GET', `/gps/tracking/driver/${driverId}`),
    getByBus: (busId) => apiRequest('GET', `/gps/tracking/bus/${busId}`),
    getHistory: (driverId, params) => apiRequest('GET', `/gps/tracking/history/${driverId}`, { params }),
    update: (data) => apiRequest('POST', '/gps/tracking/update', { data }),
  },

  // Suivi GPS Admin (nouvelles API)
  tracking: {
    getTripAttendance: async (tripId) => {
      const response = await apiRequest('GET', `/admin/tracking/trips/${tripId}/attendance`);
      return response.data;
    },
    getActiveTrips: () => apiRequest('GET', '/admin/trips', { params: { status: 'in_progress' } }),
    getStats: () => apiRequest('GET', '/admin/tracking/stats'),
    getCircuits: () => apiRequest('GET', '/admin/tracking/circuits'),
    getVehicle: (vehicleId) => apiRequest('GET', `/admin/tracking/vehicles/${vehicleId}`),
    simulate: () => apiRequest('POST', '/admin/tracking/simulate'),
    getFleet: () => apiRequest('GET', '/admin/tracking/fleet'),
    getDayView: (params) => apiRequest('GET', '/admin/tracking/fleet/day-view', { params }),
  },

  // Notifications admin
  notifications: {
    getAll: (params) => apiRequest('GET', '/admin/notifications', { params }),
    getStatistics: () => apiRequest('GET', '/admin/notifications/statistics'),
    delete: (id) => apiRequest('DELETE', `/admin/notifications/${id}`),
    // Annonces admin → parents (Premium)
    previewBroadcast: (audience, recipientType = 'parent') =>
      apiRequest('POST', '/admin/notifications/broadcasts/preview', {
        data: { audience, recipient_type: recipientType },
      }),
    sendBroadcast: (data) => apiRequest('POST', '/admin/notifications/broadcasts', { data }),
    listBroadcasts: (params) => apiRequest('GET', '/admin/notifications/broadcasts', { params }),
    getBroadcast: (id) => apiRequest('GET', `/admin/notifications/broadcasts/${id}`),
    markAsRead: (id) => apiRequest('PUT', `/admin/notifications/${id}/read`),
    markAllAsRead: () => apiRequest('PUT', '/admin/notifications/read-all'),
  },

  // Web Push admin (Firebase) — token FCM du dashboard, distinct des tokens mobile
  fcm: {
    registerToken: (data) => apiRequest('POST', '/admin/fcm/register-token', { data }),
  },

  // Gestion des écoles
  schools: {
    getCurrent: () => apiRequest('GET', '/admin/schools/current'),
    getById: (id) => apiRequest('GET', `/admin/schools/${id}`),
    update: (id, data) => apiRequest('PUT', `/admin/schools/${id}`, { data }),
    getStats: (id) => apiRequest('GET', `/admin/schools/${id}/stats`),
  },

  // Dashboard et statistiques
  dashboard: {
    // Statistiques générales
    getOverviewStats: () => apiRequest('GET', '/admin/dashboard/stats/overview'),
    getNotificationStats: () => apiRequest('GET', '/admin/dashboard/stats/notifications'),
    getStudentStats: () => apiRequest('GET', '/admin/dashboard/stats/students'),
    getParentStats: () => apiRequest('GET', '/admin/dashboard/stats/parents'),

    // Activités et statuts
    getRecentActivities: (limit = 10) => apiRequest('GET', `/admin/dashboard/activities/recent?limit=${limit}`),
    getBusStatus: () => apiRequest('GET', '/admin/dashboard/bus-status'),

    // KPIs avancés (flotte, parents détaillé, timeline notifications)
    getFleetStats: () => apiRequest('GET', '/admin/dashboard/stats/fleet'),
    getParentsDetail: () => apiRequest('GET', '/admin/dashboard/stats/parents-detail'),
    getNotificationsTimeline: () => apiRequest('GET', '/admin/dashboard/stats/notifications/timeline'),

    // Méthodes existantes (compatibilité)
    getStats: () => apiRequest('GET', '/admin/dashboard/stats'),
    getRecentActivity: () => apiRequest('GET', '/admin/dashboard/activity'),
    getAlerts: () => apiRequest('GET', '/admin/dashboard/alerts'),
  },

  // Gestion des statuts (TICKET-00)
  status: {
    updateDriver: (id, status) => apiRequest('PUT', `/admin/status/drivers/${id}`, { data: { status } }),
    updateBus: (id, status) => apiRequest('PUT', `/admin/status/buses/${id}`, { data: { status } }),
    updateParent: (id, status) => apiRequest('PUT', `/admin/status/parents/${id}`, { data: { status } }),
    updateStop: (id, status) => apiRequest('PUT', `/admin/status/stops/${id}`, { data: { status } }),
    updateRoute: (id, status) => apiRequest('PUT', `/admin/status/routes/${id}`, { data: { status } }),
    updateStudent: (id, status) => apiRequest('PUT', `/admin/status/students/${id}`, { data: { status } }),
  },

  // Authentification
  auth: {
    login: (credentials) => apiRequest('POST', '/auth/login', { data: credentials }),
    logout: () => apiRequest('POST', '/auth/logout'),
    refresh: () => apiRequest('POST', '/auth/refresh'),
    profile: () => apiRequest('GET', '/auth/profile'),
    forgotPassword: (data) => apiRequest('POST', '/auth/forgot-password', { data }),
    changePassword: (data) => apiRequest('POST', '/auth/change-password', { data }),
    verifyResetToken: (token) => apiRequest('GET', `/auth/verify-reset-token?token=${encodeURIComponent(token)}`),
    resetPassword: (data) => apiRequest('POST', '/auth/reset-password', { data }),
  },

  // Rapports analytiques (T7+)
  reports: {
    getPunctuality: (params) => apiRequest('GET', '/admin/reports/punctuality', { params }),
    getAttendance: (params) => apiRequest('GET', '/admin/reports/attendance', { params }),
    getDrivers: (params) => apiRequest('GET', '/admin/reports/drivers', { params }),
    getIncidents: (params) => apiRequest('GET', '/admin/reports/incidents', { params }),
    // Télécharge le rapport authentifié — window.open() ne peut pas porter le header
    // Authorization, donc on passe par fetch + Blob (comme documenté sur le ticket
    // d'origine "Export PDF / Excel rapports").
    exportFile: async (type, format, params = {}) => {
      const qs = new URLSearchParams({ type, format, ...params }).toString();
      const url = `${API_BASE_URL}/admin/reports/export?${qs}`;
      const token = getAuthToken();

      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        let message = `Erreur export (${response.status})`;
        try {
          const body = await response.json();
          message = body.message || body.error || message;
        } catch {
          // réponse non-JSON (ex: erreur serveur brute) — garder le message par défaut
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename =
        match?.[1] ||
        `rapport-${type}-${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
    },
  },

  // Paramètres admin
  settings: {
    getProfile: () => apiRequest('GET', '/admin/settings/profile'),
    updateProfile: (data) => apiRequest('PUT', '/admin/settings/profile', { data }),
    updateNotifications: (data) => apiRequest('PUT', '/admin/settings/notifications', { data }),
    updateSchool: (data) => apiRequest('PUT', '/admin/settings/school', { data }),
    updateSchedule: (data) => apiRequest('PUT', '/admin/settings/schedule', { data }),
  },

  holidays: {
    getAll: () => apiRequest('GET', '/admin/holidays'),
    create: (data) => apiRequest('POST', '/admin/holidays', { data }),
    update: (id, data) => apiRequest('PUT', `/admin/holidays/${id}`, { data }),
    delete: (id) => apiRequest('DELETE', `/admin/holidays/${id}`),
  },

  requests: {
    getAll: (params) => apiRequest('GET', '/admin/requests', { params }),
    getStats: () => apiRequest('GET', '/admin/requests/stats'),
    getById: (id) => apiRequest('GET', `/admin/requests/${id}`),
    getNearbyStops: (id) => apiRequest('GET', `/admin/requests/${id}/nearby-stops`),
    approve: (id, data) => apiRequest('PUT', `/admin/requests/${id}/approve`, { data }),
    reject: (id, data) => apiRequest('PUT', `/admin/requests/${id}/reject`, { data }),
  },

  thresholds: {
    get: () => apiRequest('GET', '/admin/thresholds'),
    update: (data) => apiRequest('PUT', '/admin/thresholds', { data }),
  },

  incidents: {
    getAll: (params) => apiRequest('GET', '/admin/incidents', { params }),
    getById: (id) => apiRequest('GET', `/admin/incidents/${id}`),
    update: (id, data) => apiRequest('PATCH', `/admin/incidents/${id}`, { data }),
  },

  // Force-end (superadmin impersonation uniquement) — voir docs/ideas/force-trip-ended.md
  trips: {
    forceEnd: (tripId) => apiRequest('POST', `/admin/trips/${tripId}/force-end`),
  },

  // Endpoints publics (landing page) — non authentifiés
  public: {
    submitContact: (data) => apiRequest('POST', '/public/contact', { data, skipAuth: true }),
  },
};

export default unifiedApi;
